import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../AppIcon';


const RoleBasedHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile, signOut } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const profileMenuRef = useRef(null);
  const submenuRef = useRef(null);

  const userRole = userProfile?.role || 'member';
  const userName = userProfile?.full_name || user?.email?.split('@')?.[0] || 'User';

  const roleNavigationMap = {
    member: [
      { label: 'Dashboard', path: '/member-dashboard', icon: 'LayoutDashboard' },
      { label: 'Classes', path: '/class-booking', icon: 'Calendar' },
      { label: 'Workouts', path: '/workout-programs', icon: 'Dumbbell' },
      { label: 'Gamification', path: '/member-gamification-hub', icon: 'Trophy' },
      { label: 'Messages', path: '/messaging', icon: 'MessageSquare' },
      { label: 'Sessions', path: '/session-management-center', icon: 'Monitor' },
    ],
    staff: [
      { label: 'Dashboard', path: '/staff-dashboard', icon: 'LayoutDashboard' },
      { label: 'Members', path: '/member-management', icon: 'Users' },
      { label: 'Staff Hub', path: '/staff-messaging-hub', icon: 'Radio' },
      { label: 'Messages', path: '/messaging', icon: 'MessageSquare' },
      { label: 'Sessions', path: '/session-management-center', icon: 'Monitor' },
    ],
    trainer: [
      { label: 'Dashboard', path: '/trainer-dashboard', icon: 'LayoutDashboard' },
      { label: 'Classes', path: '/class-booking', icon: 'Calendar' },
      { label: 'Messages', path: '/messaging', icon: 'MessageSquare' },
      { label: 'Sessions', path: '/session-management-center', icon: 'Monitor' },
    ],
    manager: [
      { label: 'Dashboard', path: '/staff-dashboard', icon: 'LayoutDashboard' },
      { 
        label: 'Analytics', 
        icon: 'BarChart3',
        submenu: [
          { label: 'Business Analytics', path: '/analytics-dashboard', icon: 'BarChart3' },
          { label: 'Staff Performance', path: '/staff-performance-analytics', icon: 'TrendingUp' },
        ]
      },
      { label: 'Operations', path: '/live-operations-feed', icon: 'Activity' },
      { 
        label: 'Members', 
        icon: 'Users',
        submenu: [
          { label: 'Member Management', path: '/member-management', icon: 'Users' },
          { label: 'Gamification', path: '/achievement-rewards-management', icon: 'Trophy' },
          { label: 'Retention', path: '/member-retention', icon: 'Heart' },
        ]
      },
      { label: 'Equipment', path: '/equipment-management', icon: 'Package' },
      { label: 'Lifecycle', path: '/equipment-lifecycle-management', icon: 'Activity' },
      { label: 'Maintenance', path: '/automated-maintenance-scheduling', icon: 'Calendar' },
      { label: 'Work Orders', path: '/work-order-management-system', icon: 'ClipboardList' },
      { label: 'POS', path: '/location-based-pos-inventory', icon: 'ShoppingCart' },
      { label: 'Staff Hub', path: '/staff-messaging-hub', icon: 'Radio' },
      { label: 'Financials', path: '/financial-reconciliation-dashboard', icon: 'DollarSign' },
      { label: 'Reports', path: '/automated-report-generation', icon: 'FileText' },
      { label: 'Alerts', path: '/critical-alert-management-center', icon: 'Bell' },
      { label: 'Messages', path: '/messaging', icon: 'MessageSquare' },
      { label: 'Sessions', path: '/session-management-center', icon: 'Monitor' },
    ],
    owner: [
      { label: 'Single Gym', path: '/single-gym-owner-dashboard', icon: 'Building' },
      { label: 'Subscription', path: '/subscription-management-center', icon: 'CreditCard' },
      { label: 'Payment Recovery', path: '/payment-recovery-retention-center', icon: 'RefreshCw' },
      { 
        label: 'Analytics', 
        icon: 'BarChart3',
        submenu: [
          { label: 'Business Analytics', path: '/analytics-dashboard', icon: 'BarChart3' },
          { label: 'Staff Performance', path: '/staff-performance-analytics', icon: 'TrendingUp' },
        ]
      },
      { label: 'Operations', path: '/live-operations-feed', icon: 'Activity' },
      { 
        label: 'Members', 
        icon: 'Users',
        submenu: [
          { label: 'Member Management', path: '/member-management', icon: 'Users' },
          { label: 'Gamification', path: '/achievement-rewards-management', icon: 'Trophy' },
          { label: 'Retention', path: '/member-retention', icon: 'Heart' },
        ]
      },
      { label: 'Equipment', path: '/equipment-management', icon: 'Package' },
      { label: 'Lifecycle', path: '/equipment-lifecycle-management', icon: 'Activity' },
      { label: 'Maintenance', path: '/automated-maintenance-scheduling', icon: 'Calendar' },
      { label: 'Work Orders', path: '/work-order-management-system', icon: 'ClipboardList' },
      { label: 'POS', path: '/location-based-pos-inventory', icon: 'ShoppingCart' },
      { label: 'Staff Hub', path: '/staff-messaging-hub', icon: 'Radio' },
      { label: 'Financials', path: '/financial-reconciliation-dashboard', icon: 'DollarSign' },
      { label: 'Reports', path: '/automated-report-generation', icon: 'FileText' },
      { label: 'Alerts', path: '/critical-alert-management-center', icon: 'Bell' },
      { label: 'Messages', path: '/messaging', icon: 'MessageSquare' },
      { label: 'Sessions', path: '/session-management-center', icon: 'Monitor' },
    ],
    sysadmin: [
      { label: 'System', path: '/staff-dashboard', icon: 'Shield' },
      { label: 'Sysadmin', path: '/system-administrator-dashboard', icon: 'Server' },
      { label: 'Single Gym', path: '/single-gym-owner-dashboard', icon: 'Building' },
      { 
        label: 'Analytics', 
        icon: 'BarChart3',
        submenu: [
          { label: 'Business Analytics', path: '/analytics-dashboard', icon: 'BarChart3' },
          { label: 'Staff Performance', path: '/staff-performance-analytics', icon: 'TrendingUp' },
        ]
      },
      { label: 'Operations', path: '/live-operations-feed', icon: 'Activity' },
      { 
        label: 'Members', 
        icon: 'Users',
        submenu: [
          { label: 'Member Management', path: '/member-management', icon: 'Users' },
          { label: 'Gamification', path: '/achievement-rewards-management', icon: 'Trophy' },
          { label: 'Retention', path: '/member-retention', icon: 'Heart' },
        ]
      },
      { label: 'Audit Trail', path: '/system-audit-trail-dashboard', icon: 'FileText' },
      { label: 'Compliance', path: '/compliance-monitoring-center', icon: 'ShieldCheck' },
      { label: 'Security', path: '/security-event-investigation-portal', icon: 'Shield' },
      { label: 'Equipment', path: '/equipment-management', icon: 'Package' },
      { label: 'Lifecycle', path: '/equipment-lifecycle-management', icon: 'Activity' },
      { label: 'Maintenance', path: '/automated-maintenance-scheduling', icon: 'Calendar' },
      { label: 'Work Orders', path: '/work-order-management-system', icon: 'ClipboardList' },
      { label: 'Staff Hub', path: '/staff-messaging-hub', icon: 'Radio' },
      { label: 'Financials', path: '/financial-reconciliation-dashboard', icon: 'DollarSign' },
      { label: 'Reports', path: '/automated-report-generation', icon: 'FileText' },
      { label: 'Alerts', path: '/critical-alert-management-center', icon: 'Bell' },
      { label: 'Messages', path: '/messaging', icon: 'MessageSquare' },
      { label: 'Sessions', path: '/session-management-center', icon: 'Monitor' },
    ],
  };

  const navigationItems = roleNavigationMap?.[userRole] || roleNavigationMap?.member;

  const isActivePath = (path) => {
    if (!location || !path) return false;
    return location?.pathname === path;
  };

  const isSubmenuActive = (submenu) => {
    if (!location || !submenu) return false;
    return submenu?.some(item => location?.pathname === item?.path);
  };

  const handleNavigation = (path) => {
    if (!path) return;
    navigate(path);
    setIsMobileMenuOpen(false);
    setOpenSubmenu(null);
  };

  const toggleSubmenu = (label) => {
    setOpenSubmenu(openSubmenu === label ? null : label);
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
    setIsProfileMenuOpen(false);
    setIsMobileMenuOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef?.current && !profileMenuRef?.current?.contains(event?.target)) {
        setIsProfileMenuOpen(false);
      }
      if (submenuRef?.current && !submenuRef?.current?.contains(event?.target)) {
        setOpenSubmenu(null);
      }
    };

    if (isProfileMenuOpen || openSubmenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileMenuOpen, openSubmenu]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  return (
    <>
      <header className="nav-header">
        <div className="h-full px-6 lg:px-8 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <div 
              className="flex items-center gap-3 cursor-pointer"
              onClick={() => handleNavigation(navigationItems?.[0]?.path)}
            >
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon name="Dumbbell" size={24} color="var(--color-primary)" />
              </div>
              <span className="text-xl font-heading font-semibold text-foreground">
                TrainaryGym
              </span>
            </div>

            <nav className="hidden lg:flex items-center gap-2">
              {navigationItems?.map((item) => (
                <div key={item?.label || item?.path} className="relative">
                  {item?.submenu ? (
                    // Desktop submenu dropdown
                    <div ref={openSubmenu === item?.label ? submenuRef : null}>
                      <button
                        onClick={() => toggleSubmenu(item?.label)}
                        className={`nav-item ${isSubmenuActive(item?.submenu) || openSubmenu === item?.label ? 'active' : ''}`}
                      >
                        <Icon name={item?.icon} size={18} className="mr-2" />
                        {item?.label}
                        <Icon 
                          name="ChevronDown" 
                          size={14} 
                          className={`ml-1 transition-transform duration-250 ${openSubmenu === item?.label ? 'rotate-180' : ''}`}
                        />
                      </button>
                      {openSubmenu === item?.label && (
                        <div className="dropdown-menu" style={{ minWidth: '200px' }}>
                          {item?.submenu?.map((subItem) => (
                            <button
                              key={subItem?.path}
                              onClick={() => handleNavigation(subItem?.path)}
                              className={`dropdown-item ${isActivePath(subItem?.path) ? 'bg-muted' : ''}`}
                            >
                              <Icon name={subItem?.icon} size={18} className="mr-3" />
                              {subItem?.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    // Regular navigation item
                    <button
                      onClick={() => handleNavigation(item?.path)}
                      className={`nav-item ${isActivePath(item?.path) ? 'active' : ''}`}
                    >
                      <Icon name={item?.icon} size={18} className="mr-2" />
                      {item?.label}
                    </button>
                  )}
                </div>
              ))}
            </nav>
          </div>

          <div className="flex items-center gap-4">
            <div className="relative" ref={profileMenuRef}>
              <button
                onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                className="flex items-center gap-3 px-4 py-2 rounded-lg hover:bg-muted transition-colors duration-250 focus-ring"
              >
                <div className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-medium">
                  {userName?.charAt(0)?.toUpperCase()}
                </div>
                <div className="hidden md:block text-left">
                  <div className="text-sm font-medium text-foreground">{userName}</div>
                  <div className="text-xs text-muted-foreground capitalize">{userRole}</div>
                </div>
                <Icon 
                  name="ChevronDown" 
                  size={16} 
                  className={`hidden md:block transition-transform duration-250 ${isProfileMenuOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {isProfileMenuOpen && (
                <div className="dropdown-menu">
                  <div className="px-4 py-3 border-b border-border">
                    <div className="text-sm font-medium text-popover-foreground">{userName}</div>
                    <div className="text-xs text-muted-foreground capitalize">{userRole}</div>
                  </div>
                  <button
                    onClick={() => {
                      setIsProfileMenuOpen(false);
                      navigate('/user-profile-settings');
                    }}
                    className="dropdown-item"
                  >
                    <Icon name="User" size={18} className="mr-3" />
                    Profile Settings
                  </button>
                  <button
                    onClick={() => {
                      setIsProfileMenuOpen(false);
                      navigate('/two-factor-authentication-setup');
                    }}
                    className="dropdown-item"
                  >
                    <Icon name="Shield" size={18} className="mr-3" />
                    Two-Factor Auth
                  </button>
                  <div className="border-t border-border my-2"></div>
                  <Link
                    to="/terms-and-conditions"
                    target="_blank"
                    className="dropdown-item"
                    onClick={() => setIsProfileMenuOpen(false)}
                  >
                    <Icon name="FileText" size={18} className="mr-3" />
                    Terms & Conditions
                  </Link>
                  <Link
                    to="/privacy-policy"
                    target="_blank"
                    className="dropdown-item"
                    onClick={() => setIsProfileMenuOpen(false)}
                  >
                    <Icon name="Shield" size={18} className="mr-3" />
                    Privacy Policy
                  </Link>
                  <div className="border-t border-border my-2"></div>
                  <button
                    onClick={handleLogout}
                    className="dropdown-item text-error"
                  >
                    <Icon name="LogOut" size={18} className="mr-3" />
                    Logout
                  </button>
                </div>
              )}
            </div>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-muted transition-colors duration-250 focus-ring"
              aria-label="Toggle mobile menu"
            >
              <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
            </button>
          </div>
        </div>
      </header>
      {isMobileMenuOpen && (
        <div className="mobile-menu-overlay lg:hidden" style={{ top: '64px' }}>
          <nav className="flex flex-col p-6 gap-2">
            {navigationItems?.map((item) => (
              <div key={item?.label || item?.path}>
                {item?.submenu ? (
                  // Menu item with submenu
                  (<>
                    <button
                      onClick={() => toggleSubmenu(item?.label)}
                      className={`nav-item justify-start w-full ${isSubmenuActive(item?.submenu) || openSubmenu === item?.label ? 'active' : ''}`}
                    >
                      <Icon name={item?.icon} size={20} className="mr-3" />
                      {item?.label}
                      <Icon 
                        name={openSubmenu === item?.label ? 'ChevronUp' : 'ChevronDown'} 
                        size={16} 
                        className="ml-auto"
                      />
                    </button>
                    {/* Submenu items */}
                    {openSubmenu === item?.label && (
                      <div className="ml-6 mt-2 flex flex-col gap-1">
                        {item?.submenu?.map((subItem) => (
                          <button
                            key={subItem?.path}
                            onClick={() => handleNavigation(subItem?.path)}
                            className={`nav-item justify-start text-sm ${isActivePath(subItem?.path) ? 'active' : ''}`}
                          >
                            <Icon name={subItem?.icon} size={18} className="mr-3" />
                            {subItem?.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </>)
                ) : (
                  // Regular menu item without submenu
                  (<button
                    onClick={() => handleNavigation(item?.path)}
                    className={`nav-item justify-start ${isActivePath(item?.path) ? 'active' : ''}`}
                  >
                    <Icon name={item?.icon} size={20} className="mr-3" />
                    {item?.label}
                  </button>)
                )}
              </div>
            ))}
            <div className="border-t border-border my-4"></div>
            <button
              onClick={() => {
                setIsMobileMenuOpen(false);
              }}
              className="nav-item justify-start"
            >
              <Icon name="User" size={20} className="mr-3" />
              Profile Settings
            </button>
            <button
              onClick={handleLogout}
              className="nav-item justify-start text-error"
            >
              <Icon name="LogOut" size={20} className="mr-3" />
              Logout
            </button>
          </nav>
        </div>
      )}
    </>
  );
};

export default RoleBasedHeader;